import { useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { Field, QueryState, Select, formatDate, formatDateTime, useFormErrors } from '@/components/live';
import { DropZone } from '@/components/DropZone';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { RecordDrawer, RecordTabs, ApprovalPanel, type DrawerSections } from '@/components/RecordDrawer';
import { RecordHistory } from '@/components/RecordHistory';
import { callRpc, createRow, saveRow } from '@/api/resources';
import { bucketRefusal, signedUrlsFor, uploadDocument, type BucketId } from '@/api/documents';
import { placeLabelOf } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin, normaliseIdentifier } from '@/auth/session';
import { toast } from '@/lib/toast';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { HouseLocationPicker } from '@/screens/parts/HouseLocationPicker';

/** One row of a line table: the values as typed, by column key. */
export type FormLine = Record<string, string>;
export type FormValues = Record<string, string | boolean | string[] | FormLine[]>;

/**
 * A column of a line table (owner, 2026-09-27: "value per species ... bring in table format?",
 * "species, trees estimation, plantation type, area ... shall be brought as table").
 */
export interface LineColumn {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'computed';
  options?: Array<{ id: string; label: string }>;
  optionsFor?: (form: FormValues) => Array<{ id: string; label: string }>;
  required?: boolean;
  /**
   * A computed column: its value. A number column: the value used when it is left blank, shown
   * greyed in the cell - volume from girth and height, until somebody types a measured one.
   */
  compute?: (line: FormLine, form: FormValues) => string;
  /** A computed column that is also written, under its key. */
  save?: boolean;
  showIf?: (form: FormValues) => boolean;
  /** A reference list the dropdown can be added to ("+ Add new..."): its set key. Admins only. */
  addTo?: string;
  /** Summed under the table. */
  total?: boolean;
  /** Narrow numeric cells stay narrow; a species or pattern wants room. */
  minWidth?: number;
}

export interface FieldSpec {
  key: string;
  label: string;
  /** The label when it depends on an earlier answer: "Nutrient content (N-P-K)" for a fertilizer. */
  labelFor?: (form: FormValues) => string;
  /** The hint, likewise. */
  hintFor?: (form: FormValues) => string | undefined;
  /** Sample values, likewise: fertilizer brands for a fertilizer, pesticides for a pesticide. */
  suggestionsFor?: (form: FormValues) => string[];
  type: 'text' | 'number' | 'date' | 'datetime' | 'select' | 'textarea' | 'check' | 'multi' | 'file' | 'village' | 'gps' | 'lines';
  /**
   * For a 'lines' field: its columns. A new record with several lines saves one row per line,
   * every other field repeated on each; an existing record is one row, so it shows one line.
   */
  columns?: LineColumn[];
  /** For a 'lines' field: more columns of ANOTHER line table's rows, shown as a table of their own. */
  linesOf?: string;
  /** For a table sharing another's lines: what names each row (the species chosen). */
  rowLabel?: (line: FormLine) => string;
  /**
   * For a 'lines' field: the lines are ONE record's list, kept in the column named by `key` (a
   * permit's permitted species), instead of one record per line.
   */
  linesInColumn?: boolean;
  /**
   * For a 'multi' field: options that come from the server for what the form holds now - the
   * claims a sale's plot may make. Null while there is nothing to ask about yet.
   */
  loadOptions?: (form: FormValues) => { key: string; load: () => Promise<Array<{ id: string; label: string }>>; emptyText?: string } | null;
  /** For a 'lines' field: what a new line starts with, and what the Add button says. */
  defaultLine?: FormLine;
  addLineLabel?: string;
  /**
   * For a 'gps' field: its own key holds the latitude and this one the longitude. It is drawn as a
   * map with "Use my location" (owner, 2026-09-27: "add site: GPS map view missing").
   */
  lngKey?: string;
  /** For a 'multi' field: a list with an On / Off switch per option instead of chips. */
  toggles?: boolean;
  /**
   * For a 'village' field, the level actually being chosen. A farmer's plot wants a village; an
   * office wants the state it serves, because that is what the letterhead matches on.
   */
  geographyType?: 'state' | 'district' | 'block' | 'village';
  options?: Array<{ id: string; label: string }>;
  /**
   * Options that depend on an answer already given - the species lines of the plot just chosen,
   * and nothing else's. Inside a plot record the parent settles that; on a register spanning
   * plots the form has to. When present it replaces `options`.
   */
  optionsFor?: (form: FormValues) => Array<{ id: string; label: string }>;
  /**
   * The prompt inside an empty dropdown, when what to say depends on the form. A species list
   * that is empty because no plot has been chosen yet and one that is empty because the plot has
   * no tree inventory are the same empty list and two different problems. When present it wins
   * over `placeholder`.
   */
  placeholderFor?: (form: FormValues) => string;
  required?: boolean | ((form: FormValues) => boolean);
  hint?: string;
  placeholder?: string;
  /** Shown only when this returns true; a hidden field is saved as empty. */
  showIf?: (form: FormValues) => boolean;
  /** Spans the whole row instead of half. */
  wide?: boolean;
  min?: string;
  max?: string;
  /** For a file: the bucket; the uploaded document's id is saved in `key`. */
  bucket?: BucketId;
  accept?: string;
  /** A section heading shown above this field. */
  section?: string;
  /** Sample values offered while typing (the field still takes any text). */
  suggestions?: string[];
  /**
   * The keypad a phone should offer. A `number` field already asks for `decimal`; this is for the
   * ones that are text because they can carry a leading zero or a separator - a PIN code, a phone
   * number - and would otherwise open a full alphabetic keyboard in the field. A licence, invoice
   * or vehicle number is alphanumeric here and deliberately left alone.
   */
  inputMode?: 'numeric' | 'decimal' | 'tel' | 'email';
  /** A reference list the dropdown can be added to ("+ Add new..."): its set key. */
  addTo?: string;
  /**
   * The dropdown takes a value typed in when it is not listed ("+ Add new..."), stored as the text
   * itself - for a free-text column offered as a list of what is already used (a laboratory).
   */
  allowNew?: boolean;
}

const FULL_WIDTH: FieldSpec['type'][] = ['textarea', 'multi', 'file', 'village', 'gps', 'lines'];

/**
 * One row per line: every other value repeated on each (a sapling issue of three species is three
 * issues from one handover). A form without a line table is one row, as before.
 */
export function expandLines(fields: FieldSpec[], values: Record<string, unknown>): Array<Record<string, unknown>> {
  const master = fields.find((field) => field.type === 'lines' && !field.linesOf && !field.linesInColumn);
  if (!master) return [values];
  const { [master.key]: lines, ...rest } = values;
  const list = Array.isArray(lines) ? lines as Array<Record<string, unknown>> : [];
  return list.length ? list.map((line) => ({ ...rest, ...line })) : [rest];
}

/** A line's numbers as the table works with them: blank is null, not zero. */
export const lineNumber = (text: string | undefined): number | null => {
  const value = String(text ?? '').trim();
  return value === '' || Number.isNaN(Number(value)) ? null : Number(value);
};

/** A stored file, opened through a short-lived signed link. */
function StoredFileLink({ documentId }: { documentId: string }) {
  const link = useQuery({ queryKey: ['signed-urls', documentId], queryFn: () => signedUrlsFor([documentId]), enabled: isSupabaseConfigured });
  const url = link.data?.[documentId];
  return url ? <a href={url} rel="noreferrer" target="_blank"><ExternalLink size={14} /> Open file</a> : <span className="muted">File on record</span>;
}

/**
 * A checklist whose options come from the server for what the form holds now (the claims open to a
 * sale's plot). A choice that is no longer offered stays listed, marked, so it is not silently lost.
 */
function LoadedChecklist({ source, chosen, onChange, placeholder }: {
  source: { key: string; load: () => Promise<Array<{ id: string; label: string }>>; emptyText?: string } | null;
  chosen: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const options = useQuery({ queryKey: ['loaded-options', source?.key ?? 'none'], queryFn: () => source!.load(), enabled: Boolean(source) && isSupabaseConfigured });
  if (!source) return <span className="muted">{placeholder ?? 'Nothing to choose from yet.'}</span>;
  if (options.isLoading) return <span className="muted">Loading...</span>;
  const listed = options.data ?? [];
  const stale = chosen.filter((id) => !listed.some((option) => option.id === id)).map((id) => ({ id, label: `${id} (no longer open to this record)` }));
  const all = [...listed, ...stale];
  if (all.length === 0) return <span className="muted">{source.emptyText ?? 'Nothing is open to this record.'}</span>;
  return (
    <ul className="toggle-list">
      {all.map((option) => {
        const on = chosen.includes(option.id);
        return (
          <li key={option.id}>
            <span>{option.label}</span>
            <button aria-pressed={on} className="onoff-toggle" type="button"
                    onClick={() => onChange(on ? chosen.filter((id) => id !== option.id) : [...chosen, option.id])}>
              {on ? 'On' : 'Off'}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** "Village, Block, District, State" for a geography id. */
function PlaceName({ geographyId }: { geographyId: string }) {
  const place = useQuery({ queryKey: ['place-of', geographyId], queryFn: () => placeLabelOf(geographyId), enabled: isSupabaseConfigured });
  return <>{place.data ?? '...'}</>;
}

/** A section name as an id fragment, so a tab and its panel can point at each other. */
const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/**
 * The form engine every spec-driven record uses (child tables and registers alike): values as
 * strings in the form and typed on save, required fields checked together and listed in the error
 * dialog, hidden fields saved empty, and files uploaded before the row is written.
 */
export function useSpecForm(fields: FieldSpec[], defaults?: FormValues) {
  const { profile } = useAuth();
  const { errors, check, clear, set: setErrors } = useFormErrors();
  const blank = (): FormValues => Object.fromEntries(fields.map((field) => [
    field.key, defaults?.[field.key] ?? (field.type === 'check' ? false : field.type === 'multi' ? []
      : field.type === 'lines' ? (field.linesOf ? [] : [{ ...(field.defaultLine ?? {}) }]) : ''),
  ]));
  // A saved record is one row, so it is one line: no adding or removing lines while editing it.
  const [singleLine, setSingleLine] = useState(false);
  // Files already stored by an earlier attempt at this save. A save that failed after its first
  // uploads (found 2026-09-28: a docket refused on its fourth file) would otherwise upload them again
  // on retry and be told "this exact file is already stored" - so the retry reuses them.
  const uploaded = useRef(new Map<File, string>());
  // A form with per-record lines opens a saved record as its one line; a list kept in a column does not.
  const fansOut = fields.some((field) => field.type === 'lines' && !field.linesOf && !field.linesInColumn);
  /** Every column of a line table, its own and those of the tables that share its lines. */
  const lineColumnsOf = (masterKey: string) => fields
    .filter((field) => field.type === 'lines' && (field.key === masterKey || field.linesOf === masterKey))
    .flatMap((field) => (field.columns ?? []).map((column) => ({ column, field })));
  const [form, setForm] = useState<FormValues>(blank);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [adding, setAdding] = useState<Record<string, string | undefined>>({});
  const [added, setAdded] = useState<Record<string, Array<{ id: string; label: string }>>>({});

  async function addOption(field: FieldSpec) {
    const label = (adding[field.key] ?? '').trim();
    if (!label) return;
    if (!field.addTo && field.allowNew) {
      setAdded((now) => ({ ...now, [field.key]: [...(now[field.key] ?? []), { id: label, label }] }));
      set(field.key, label);
      setAdding((now) => ({ ...now, [field.key]: undefined }));
      return;
    }
    if (!field.addTo) return;
    try {
      const id = await callRpc<string>('fn_add_reference_value', { p_set_key: field.addTo, p_label: label });
      setAdded((now) => ({ ...now, [field.key]: [...(now[field.key] ?? []), { id, label }] }));
      set(field.key, id);
      setAdding((now) => ({ ...now, [field.key]: undefined }));
      toast.success(`"${label}" added to the list.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'It could not be added.');
    }
  }

  const visible = (field: FieldSpec) => !field.showIf || field.showIf(form);
  const isRequired = (field: FieldSpec) => (typeof field.required === 'function' ? field.required(form) : Boolean(field.required));

  function load(row: Record<string, unknown> | null, extra?: FormValues) {
    clear();
    setFiles({});
    setSingleLine(Boolean(row) && fansOut);
    if (!row) { setForm(blank()); return; }
    const mapped: FormValues = Object.fromEntries(fields.flatMap((field): Array<[string, FormValues[string]]> => {
      if (field.type === 'lines') {
        if (field.linesOf) return [[field.key, []]];
        if (field.linesInColumn) {
          const stored = Array.isArray(row[field.key]) ? row[field.key] as Array<Record<string, unknown>> : [];
          const lines = stored.map((item) => Object.fromEntries(Object.entries(item).map(([key, value]) => [key, value === null || value === undefined ? '' : String(value)])));
          return [[field.key, lines.length ? lines : [{ ...(field.defaultLine ?? {}) }]]];
        }
        const line: FormLine = Object.fromEntries(lineColumnsOf(field.key)
          .filter(({ column }) => column.type !== 'computed')
          .map(({ column }) => [column.key, row[column.key] === null || row[column.key] === undefined ? '' : String(row[column.key])]));
        return [[field.key, [line]]];
      }
      if (field.type === 'gps' && field.lngKey) {
        const text = (key: string) => (row[key] === null || row[key] === undefined ? '' : String(row[key]));
        return [[field.key, text(field.key)], [field.lngKey, text(field.lngKey)]];
      }
      return [(() => {
      const value = row[field.key];
      if (field.type === 'check') return [field.key, Boolean(value)];
      if (field.type === 'multi') return [field.key, Array.isArray(value) ? value.map(String) : []];
      if (field.type === 'datetime' && typeof value === 'string') return [field.key, toLocalInput(value)];
      return [field.key, value === null || value === undefined ? '' : String(value)];
      })()];
    }));
    setForm({ ...mapped, ...(extra ?? {}) });
  }

  /** Check, upload and type the values; null when the form has problems (already shown). */
  async function values(validate?: (form: FormValues) => Record<string, string | undefined>, ownerId?: string): Promise<Record<string, unknown> | null> {
    const problems: Record<string, string | undefined> = {};
    for (const field of fields) {
      if (!visible(field)) continue;
      const value = form[field.key];
      if (field.type === 'lines') {
        if (field.linesOf) continue;
        const lines = filledLines(field.key);
        if (lines.length === 0 && isRequired(field)) { problems[field.key] = `Add at least one line to ${labelOf(field).toLowerCase()}.`; continue; }
        const columns = liveColumns(field.key);
        lines.forEach((line, index) => {
          for (const { column } of columns) {
            const text = String(line[column.key] ?? '').trim();
            if (column.required && text === '' && !problems[field.key]) problems[field.key] = `Line ${index + 1}: ${column.label} is required.`;
            if (column.type === 'number' && text !== '' && Number.isNaN(Number(text)) && !problems[field.key]) {
              problems[field.key] = `Line ${index + 1}: ${column.label} must be a number.`;
            }
          }
        });
        continue;
      }
      if (isRequired(field)) {
        const empty = field.type === 'file' ? !files[field.key] && !value
          : field.type === 'multi' ? (value as string[]).length === 0
            : field.type === 'check' ? false : String(value ?? '').trim() === '';
        if (empty) problems[field.key] = `${labelOf(field)} is required.`;
      }
      if (field.type === 'number' && String(value ?? '').trim() !== '' && Number.isNaN(Number(value))) {
        problems[field.key] = `${labelOf(field)} must be a number.`;
      }
      // A contact field is checked because it SAYS it is one. Doing this per screen is how a
      // field gets missed - sites' in-charge phone was, and its placeholder taught people to
      // type a bare ten digits that the column now refuses (migration 0135). Declaring
      // inputMode 'tel' or 'email' is enough; anything added later is covered without anyone
      // remembering to wire it up.
      if ((field.inputMode === 'tel' || field.inputMode === 'email') && String(value ?? '').trim() !== '') {
        const result = normaliseIdentifier(field.inputMode === 'email' ? 'email' : 'sms', String(value));
        if ('error' in result) problems[field.key] = result.error;
      }
    }
    for (const [key, message] of Object.entries(validate?.(form) ?? {})) {
      if (message && !problems[key]) problems[key] = message;
    }
    if (!check(problems) || !profile?.organization) return null;
    const out: Record<string, unknown> = {};
    for (const field of fields) {
      const shown = visible(field);
      let value = form[field.key];
      // Stored as the column expects: a bare ten digits becomes +91..., an address is lowercased.
      // Validating without normalising would leave the form satisfied and the write refused.
      if (shown && (field.inputMode === 'tel' || field.inputMode === 'email') && String(value ?? '').trim() !== '') {
        const result = normaliseIdentifier(field.inputMode === 'email' ? 'email' : 'sms', String(value));
        if ('value' in result) value = result.value;
      }
      if (field.type === 'file') {
        const file = files[field.key];
        if (file && shown) {
          let id = uploaded.current.get(file);
          if (!id) {
            id = (await uploadDocument({ file, bucket: field.bucket ?? 'plot-evidence', organizationId: profile.organization.id, ownerId: ownerId ?? profile.organization.id })).id;
            uploaded.current.set(file, id);
          }
          out[field.key] = id;
        } else if (!shown) {
          out[field.key] = null;
        }
        continue;
      }
      if (field.type === 'lines') {
        if (field.linesOf) continue;
        // Every column of every table sharing these lines; a hidden one is written empty.
        const all = lineColumnsOf(field.key);
        const live = new Set(liveColumns(field.key).map(({ column }) => column.key));
        out[field.key] = filledLines(field.key).map((line) => Object.fromEntries(all
          .filter(({ column }) => column.type !== 'computed' || column.save)
          .map(({ column }) => {
            if (!live.has(column.key)) return [column.key, null];
            const typed = String(line[column.key] ?? '').trim();
            const text = typed === '' && column.compute ? column.compute(line, form) : typed;
            if (column.type === 'number' || column.type === 'computed') return [column.key, lineNumber(text)];
            return [column.key, text === '' ? null : text];
          })));
        continue;
      }
      if (!shown) { out[field.key] = field.type === 'check' ? false : field.type === 'multi' ? [] : null; continue; }
      if (field.type === 'gps') {
        const number = (text: unknown) => (String(text ?? '').trim() === '' ? null : Number(text));
        out[field.key] = number(value);
        if (field.lngKey) out[field.lngKey] = number(form[field.lngKey]);
        continue;
      }
      if (field.type === 'check') out[field.key] = Boolean(value);
      else if (field.type === 'multi') out[field.key] = value;
      else if (field.type === 'number') out[field.key] = String(value).trim() === '' ? null : Number(value);
      else if (field.type === 'datetime') out[field.key] = String(value ?? '').trim() === '' ? null : new Date(String(value)).toISOString();
      else out[field.key] = String(value ?? '').trim() === '' ? null : String(value).trim();
    }
    return out;
  }

  /**
   * Change a field, and drop the error that was on it.
   *
   * `check()` sets every error at submit and only `clear()` removes them, so before this a
   * message stayed under a field after the person had fixed it - and on a form with sections,
   * the tab kept its red dot too. The only way to find out what was still wrong was to press
   * Save again and read the dialog, which is the thing the inline messages exist to avoid.
   *
   * It clears on CHANGE rather than re-validating: whether the new value is good is a question
   * for Save, and answering it on every keystroke would put an error under a field the moment
   * somebody started typing in it.
   */
  const set = (key: string, value: FormValues[string]) => {
    setForm((now) => ({ ...now, [key]: value }));
    setErrors((now) => {
      if (!now[key]) return now;
      const next = { ...now };
      delete next[key];
      return next;
    });
  };

  const labelOf = (field: FieldSpec) => (field.labelFor ? field.labelFor(form) : field.label);
  const linesOf = (masterKey: string) => (Array.isArray(form[masterKey]) ? form[masterKey] as FormLine[] : []);
  /** The columns shown now: a hidden table's, or a hidden column's, are not asked for. */
  function liveColumns(masterKey: string) {
    return lineColumnsOf(masterKey).filter(({ column, field }) => visible(field) && (!column.showIf || column.showIf(form)));
  }
  /** The lines with something typed in them; a blank line left at the foot is not a record. */
  function filledLines(masterKey: string) {
    return linesOf(masterKey).filter((line) => Object.values(line).some((text) => String(text ?? '').trim() !== ''));
  }
  const cellKey = (field: FieldSpec, index: number, column: LineColumn) => `${field.key}.${index}.${column.key}`;

  async function addLineOption(field: FieldSpec, index: number, column: LineColumn) {
    const key = cellKey(field, index, column);
    const label = (adding[key] ?? '').trim();
    if (!label || !column.addTo) return;
    try {
      const id = await callRpc<string>('fn_add_reference_value', { p_set_key: column.addTo, p_label: label });
      setAdded((now) => ({ ...now, [`col:${column.key}`]: [...(now[`col:${column.key}`] ?? []), { id, label }] }));
      setLine(field, index, column.key, id);
      setAdding((now) => ({ ...now, [key]: undefined }));
      toast.success(`"${label}" added to the list.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'It could not be added.');
    }
  }

  function setLine(field: FieldSpec, index: number, key: string, value: string) {
    const masterKey = field.linesOf ?? field.key;
    set(masterKey, linesOf(masterKey).map((line, at) => (at === index ? { ...line, [key]: value } : line)));
  }

  const columnOptions = (column: LineColumn) => {
    const base = column.optionsFor ? column.optionsFor(form) : column.options ?? [];
    return [...base, ...(added[`col:${column.key}`] ?? []).filter((extra) => !base.some((option) => option.id === extra.id))];
  };

  /** A line table: one row per species (or whatever the lines are), with totals at the foot. */
  function lineTable(field: FieldSpec, readOnly = false) {
    const masterKey = field.linesOf ?? field.key;
    const master = fields.find((candidate) => candidate.key === masterKey);
    const lines = readOnly ? filledLines(masterKey) : linesOf(masterKey);
    const columns = (field.columns ?? []).filter((column) => !column.showIf || column.showIf(form));
    const shownText = (line: FormLine, column: LineColumn) => {
      const typed = String(line[column.key] ?? '');
      if (column.type === 'select') return columnOptions(column).find((option) => option.id === typed)?.label ?? (typed || '--');
      if (column.type === 'computed') return column.compute?.(line, form) || '--';
      return typed || column.compute?.(line, form) || '--';
    };
    const totalOf = (column: LineColumn) => lines.reduce((sum, line) => {
      const text = column.type === 'computed' || String(line[column.key] ?? '').trim() === '' ? column.compute?.(line, form) : line[column.key];
      return sum + (lineNumber(text) ?? 0);
    }, 0);
    const growable = !readOnly && !field.linesOf && (!singleLine || Boolean(field.linesInColumn));
    if (field.linesOf && lines.length === 0) return <p className="muted">Add a line under {master ? labelOf(master) : 'the first table'} first.</p>;
    return (
      <div className="line-table-wrap">
        <table className="simple-table line-table">
          <thead>
            <tr>
              {field.linesOf && <th>{field.rowLabel ? 'Line' : '#'}</th>}
              {columns.map((column) => (
                <th className={column.type === 'number' || column.type === 'computed' ? 'num' : undefined} key={column.key}
                    style={column.minWidth ? { minWidth: column.minWidth } : undefined}>
                  {column.label}{column.required && !readOnly ? ' *' : ''}
                </th>
              ))}
              {growable && <th aria-label="Remove" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                {field.linesOf && <td className="line-label">{field.rowLabel?.(line) || `Line ${index + 1}`}</td>}
                {columns.map((column) => {
                  const numeric = column.type === 'number' || column.type === 'computed';
                  if (readOnly || column.type === 'computed') {
                    return <td className={numeric ? 'num' : undefined} key={column.key}>{shownText(line, column)}</td>;
                  }
                  const key = cellKey(field, index, column);
                  if (column.type === 'select') {
                    if (column.addTo && adding[key] !== undefined) {
                      return (
                        <td key={column.key}>
                          <div className="select-with-add">
                            <input autoFocus onChange={(event) => setAdding((now) => ({ ...now, [key]: event.target.value }))}
                                   onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addLineOption(field, index, column); } }}
                                   placeholder="New value" value={adding[key]} />
                            <button className="primary" onClick={() => void addLineOption(field, index, column)} type="button">Add</button>
                            <button className="cancel" onClick={() => setAdding((now) => ({ ...now, [key]: undefined }))} type="button">Cancel</button>
                          </div>
                        </td>
                      );
                    }
                    const options = columnOptions(column);
                    return (
                      <td key={column.key} style={column.minWidth ? { minWidth: column.minWidth } : undefined}>
                        <Select aria-label={`${column.label}, line ${index + 1}`}
                                onChange={(next) => (next === '__add__' ? setAdding((now) => ({ ...now, [key]: '' })) : setLine(field, index, column.key, next))}
                                options={column.addTo && isAdmin(profile) ? [...options, { id: '__add__', label: '+ Add new...' }] : options}
                                placeholder="Select..." value={String(line[column.key] ?? '')} />
                      </td>
                    );
                  }
                  return (
                    <td className={numeric ? 'num' : undefined} key={column.key}>
                      <input aria-label={`${column.label}, line ${index + 1}`} inputMode={column.type === 'number' ? 'decimal' : undefined}
                             onChange={(event) => setLine(field, index, column.key, event.target.value)}
                             placeholder={column.compute?.(line, form) || undefined} value={String(line[column.key] ?? '')} />
                    </td>
                  );
                })}
                {growable && (
                  <td>
                    <button aria-label={`Remove line ${index + 1}`} className="icon-only" disabled={lines.length === 1}
                            onClick={() => set(masterKey, lines.filter((_, at) => at !== index))} title="Remove line" type="button">
                      <Trash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {columns.some((column) => column.total) && lines.length > 0 && (
            <tfoot>
              <tr>
                {field.linesOf && <td />}
                {columns.map((column, at) => (
                  <td className={column.total ? 'num' : undefined} key={column.key}>
                    {column.total ? totalOf(column).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : at === 0 && !field.linesOf ? <strong>Total</strong> : null}
                  </td>
                ))}
                {growable && <td />}
              </tr>
            </tfoot>
          )}
        </table>
        {growable && (
          <button className="line-table-add" onClick={() => set(masterKey, [...lines, { ...(field.defaultLine ?? {}) }])} type="button">
            <Plus size={14} /> {field.addLineLabel ?? 'Add line'}
          </button>
        )}
      </div>
    );
  }
  const suggestionsOf = (field: FieldSpec) => (field.suggestionsFor ? field.suggestionsFor(form) : field.suggestions);

  function input(field: FieldSpec) {
    const value = form[field.key];
    switch (field.type) {
      case 'lines':
        return lineTable(field);
      case 'select': {
        const base = field.optionsFor ? field.optionsFor(form) : field.options ?? [];
        const options = [...base, ...(added[field.key] ?? []).filter((extra) => !base.some((option) => option.id === extra.id))];
        // A free-text value already saved stays visible even when it is not one of the listed ones.
        if (field.allowNew && value && typeof value === 'string' && !options.some((option) => option.id === value)) options.push({ id: value, label: value });
        if ((field.addTo || field.allowNew) && adding[field.key] !== undefined) {
          return (
            <div className="select-with-add">
              <input autoFocus onChange={(event) => setAdding((now) => ({ ...now, [field.key]: event.target.value }))}
                     onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addOption(field); } }}
                     placeholder="New value" value={adding[field.key]} />
              <button className="primary" onClick={() => void addOption(field)} type="button">Add</button>
              <button className="cancel" onClick={() => setAdding((now) => ({ ...now, [field.key]: undefined }))} type="button">Cancel</button>
            </div>
          );
        }
        // An empty list says so. "Select..." over nothing reads as a control that failed to load,
        // and on a REQUIRED picker - the accident register asks for a worker and defaults the person
        // affected to "worker" - it is a dead end until the reader guesses that the register behind
        // it is empty. `field.placeholder` still wins where a screen has said something better.
        const placeholder = field.placeholderFor?.(form) ?? field.placeholder
          ?? (options.length === 0 ? `No ${field.label.toLowerCase()} on record yet` : 'Select...');
        return (
          <Select onChange={(next) => (next === '__add__' ? setAdding((now) => ({ ...now, [field.key]: '' })) : set(field.key, next))}
                  options={(field.addTo && isAdmin(profile)) || field.allowNew ? [...options, { id: '__add__', label: '+ Add new...' }] : options}
                  placeholder={placeholder} value={String(value ?? '')} />
        );
      }
      case 'textarea':
        return <textarea onChange={(event) => set(field.key, event.target.value)} placeholder={field.placeholder} rows={2} value={String(value ?? '')} />;
      case 'check':
        return (
          <label className="check-inline">
            <input checked={Boolean(value)} onChange={(event) => set(field.key, event.target.checked)} type="checkbox" /> {field.placeholder ?? 'Yes'}
          </label>
        );
      case 'multi': {
        const chosen = value as string[];
        if (field.loadOptions) {
          return <LoadedChecklist chosen={chosen} onChange={(next) => set(field.key, next)} placeholder={field.placeholder}
                                  source={field.loadOptions(form)} />;
        }
        if (field.toggles) {
          const options = field.optionsFor ? field.optionsFor(form) : field.options ?? [];
          if (options.length === 0) return <span className="muted">{field.placeholder ?? 'Nothing to choose from yet.'}</span>;
          return (
            <ul className="toggle-list">
              {options.map((option) => {
                const on = chosen.includes(option.id);
                return (
                  <li key={option.id}>
                    <span>{option.label}</span>
                    <button aria-pressed={on} className="onoff-toggle" type="button"
                            onClick={() => set(field.key, on ? chosen.filter((id) => id !== option.id) : [...chosen, option.id])}>
                      {on ? 'On' : 'Off'}
                    </button>
                  </li>
                );
              })}
            </ul>
          );
        }
        return (
          <div className="chip-picker">
            {(field.optionsFor ? field.optionsFor(form) : field.options ?? []).map((option) => (
              <label className={`chip-option${chosen.includes(option.id) ? ' is-on' : ''}`} key={option.id}>
                <input checked={chosen.includes(option.id)} type="checkbox"
                       onChange={(event) => set(field.key, event.target.checked ? [...chosen, option.id] : chosen.filter((id) => id !== option.id))} />
                {option.label}
              </label>
            ))}
          </div>
        );
      }
      case 'gps': {
        const lat = Number(form[field.key]);
        const lng = Number(field.lngKey ? form[field.lngKey] : NaN);
        const point = String(form[field.key] ?? '') !== '' && Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng, accuracy: null } : null;
        return (
          <HouseLocationPicker owner="record" subject="place" onChange={(next) => {
                                 set(field.key, next ? next.lat.toFixed(6) : '');
                                 if (field.lngKey) set(field.lngKey, next ? next.lng.toFixed(6) : '');
                               }}
                               value={point} />
        );
      }
      case 'file':
        return (
          <DropZone accept={field.accept ?? 'image/*,application/pdf'}
                    buttonLabel={files[field.key] ? 'Change file' : value ? 'Replace file' : 'Choose file'}
                    label={files[field.key] ? `${files[field.key]!.name} - drag another, or` : value ? 'A file is on file. Drag a new one, or' : 'Drag the file here, or'}
                    onFile={(file) => {
                      // Refused at pick time, not at Save: the officer can take another photo while still on the plot.
                      const refusal = bucketRefusal(field.bucket ?? 'plot-evidence', file);
                      if (refusal) { toast.error(refusal); return; }
                      setFiles((now) => ({ ...now, [field.key]: file }));
                    }} />
        );
      default:
        return (
          <>
            <input inputMode={field.inputMode ?? (field.type === 'number' ? 'decimal' : undefined)} list={suggestionsOf(field) ? `dl-${field.key}` : undefined}
                   max={field.max} min={field.min} onChange={(event) => set(field.key, event.target.value)}
                   placeholder={field.placeholder ?? (suggestionsOf(field) ? `e.g. ${suggestionsOf(field)![0]}` : undefined)}
                   type={field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : 'text'} value={String(value ?? '')} />
            {suggestionsOf(field) && <datalist id={`dl-${field.key}`}>{suggestionsOf(field)!.map((item) => <option key={item} value={item} />)}</datalist>}
          </>
        );
    }
  }

  const [section, setSection] = useState<string | null>(null);

  /**
   * The fields, half-width ones paired in rows; wide fields, text areas and pickers take a row.
   * Tabbed (a register's drawer), each `section` is a tab of its own; the fields before the first
   * section are the first tab. A problem in another tab still shows in the error dialog.
   */
  /** The form's sections as tabs, or null when it has none - for a drawer to show in its own tab row. */
  function sectionTabs() {
    const shownFields = fields.filter(visible);
    const sectionNames = [...new Set(shownFields.map((field) => field.section).filter((name): name is string => Boolean(name)))];
    if (sectionNames.length === 0) return null;
    const firstName = shownFields[0]?.section ? sectionNames[0]! : 'General';
    const tabs = shownFields[0]?.section ? sectionNames : [firstName, ...sectionNames];
    const current = section && tabs.includes(section) ? section : tabs[0]!;
    const errorIn = (name: string) => { let run = firstName; return shownFields.some((field) => { if (field.section) run = field.section; return run === name && Boolean(errors[field.key]); }); };
    return { shownFields, firstName, tabs, current, errorIn };
  }

  function render(tabbed = false, tabsInDrawer = false, readOnly = false) {
    const shownFields = fields.filter(visible);
    const sectioned = tabbed ? sectionTabs() : null;
    if (sectioned) {
      const { firstName, tabs, current, errorIn } = sectioned;
      let running = firstName;
      const inTab = shownFields.filter((field) => { if (field.section) running = field.section; return running === current; });
      if (readOnly) return viewList(inTab);
      if (tabsInDrawer) return <>{layout(inTab.map((field) => ({ ...field, section: undefined })))}</>;
      return (
        <>
          <div className="form-tabs" role="tablist" aria-label="Form sections">
            {tabs.map((name) => (
              <button aria-controls="form-section-panel" aria-selected={name === current}
                      className={`${name === current ? 'active' : ''}${errorIn(name) ? ' has-error' : ''}`}
                      id={`form-section-tab-${slug(name)}`}
                      key={name} onClick={() => setSection(name)} role="tab" type="button">{name}</button>
            ))}
          </div>
          {/* A tab with nothing marked as its panel announces "tab 1 of 5" over content a reader
              cannot then find. One panel, relabelled as the section changes. */}
          <div aria-labelledby={`form-section-tab-${slug(current)}`} id="form-section-panel" role="tabpanel">
            {layout(inTab.map((field) => ({ ...field, section: undefined })))}
          </div>
        </>
      );
    }
    return readOnly ? viewList(shownFields) : layout(shownFields);
  }

  /**
   * The record read, not edited (owner, 2026-09-27: "on click on datagrid ... shall show detailed
   * view. with an option to edit"): each field's label and its value as a person reads it - the
   * option's name, not its id; a date, not an ISO string.
   */
  function viewList(list: FieldSpec[]) {
    return (
      <dl className="detail-grid spec-view">
        {list.map((field) => (
          <div className={field.wide || FULL_WIDTH.includes(field.type) ? 'spec-view-wide' : undefined} key={field.key}>
            <dt>{labelOf(field)}</dt>
            <dd>{shownValue(field)}</dd>
          </div>
        ))}
      </dl>
    );
  }

  function shownValue(field: FieldSpec): ReactNode {
    const value = form[field.key];
    const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    const options = () => [...(field.optionsFor ? field.optionsFor(form) : field.options ?? []), ...(added[field.key] ?? [])];
    switch (field.type) {
      case 'lines': return lineTable(field, true);
      case 'check': return value ? 'Yes' : 'No';
      case 'select': return empty ? '--' : options().find((option) => option.id === value)?.label ?? String(value);
      case 'multi': return empty ? '--' : (value as string[]).map((id) => options().find((option) => option.id === id)?.label ?? id).join(', ');
      case 'date': return empty ? '--' : formatDate(String(value));
      case 'datetime': return empty ? '--' : formatDateTime(String(value));
      case 'file': return empty ? 'No file' : <StoredFileLink documentId={String(value)} />;
      case 'village': return empty ? '--' : <PlaceName geographyId={String(value)} />;
      case 'gps': {
        const lng = field.lngKey ? form[field.lngKey] : '';
        if (empty || !lng) return '--';
        return <a href={`https://www.google.com/maps?q=${value},${lng}`} rel="noreferrer" target="_blank">{String(value)}, {String(lng)} <ExternalLink size={13} /></a>;
      }
      default: return empty ? '--' : String(value);
    }
  }

  function layout(list: FieldSpec[]) {
    const groups: FieldSpec[][] = [];
    for (const field of list) {
      const full = field.wide || FULL_WIDTH.includes(field.type) || Boolean(field.section);
      const last = groups[groups.length - 1];
      const first = last?.[0];
      if (!full && last && first && last.length === 1 && !(first.wide || FULL_WIDTH.includes(first.type))) last.push(field);
      else groups.push([field]);
    }
    return groups.map((group) => (
      <div key={group.map((field) => field.key).join('-')}>
        {group[0]?.section && <div className="section-heading"><h3>{group[0].section}</h3></div>}
        <div className={group.length > 1 ? 'field-row' : undefined}>
          {/* The picker was given neither the field's label nor its required flag, so every one
              of these read "Village *" whatever the field actually asked for. */}
          {group.map((field) => field.type === 'village' ? (
            <GeographyPicker
              error={errors[field.key]}
              geographyType={field.geographyType}
              hint={field.hint}
              key={field.key}
              label={labelOf(field)}
              onChange={(geographyId) => set(field.key, geographyId)}
              required={isRequired(field)}
              value={String(form[field.key] ?? '')} />
          ) : (
            <Field error={errors[field.key]} hint={field.hintFor ? field.hintFor(form) : field.hint} key={field.key} label={labelOf(field)} required={isRequired(field)}>
              {input(field)}
            </Field>
          ))}
        </div>
      </div>
    ));
  }

  /** The sections as a drawer's tab row wants them, with a red count where a tab holds a problem. */
  function drawerSections(): DrawerSections | undefined {
    const sectioned = sectionTabs();
    if (!sectioned) return undefined;
    return {
      items: sectioned.tabs.map((name) => ({
        id: name,
        label: <>{name}{sectioned.errorIn(name) && <span className="tab-error-count">!</span>}</>,
      })),
      active: sectioned.current,
      onChange: setSection,
    };
  }

  return { form, setForm, load, values, render, clear, drawerSections };
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A plot's (or a worker's) child records as a list with an inline form: add, edit and delete
 * (owner, 2026-09-21: inputs, irrigation, soil tests, growth, pests, silviculture ... "as child
 * table"). A deletion keeps the row in the audit history with its reason.
 */
export function ChildRecords<T extends { id: string }>({
  title, view, table, filters, parent, fields, defaults, summary, emptyText, screenKey, validate, fromRow, ownerId, addLabel, intro, orderBy, extra, invalidate = [],
}: {
  /** Other lists that count these rows and so must refresh with them (a harvest's photo count). */
  invalidate?: string[];
  title: string;
  view: string;
  /** The underlying table ('ops.plot_soil_tests'), for the Approval & lock and history tabs. */
  table?: string;
  filters: Record<string, string>;
  /** Written on every new row: the parent keys (plot_id, worker_id ...). */
  parent: Record<string, string>;
  fields: FieldSpec[];
  defaults?: FormValues;
  summary: (row: T) => { title: ReactNode; detail?: ReactNode; badge?: ReactNode };
  emptyText: string;
  screenKey: string;
  validate?: (form: FormValues) => Record<string, string | undefined>;
  /** Row -> form, when a column does not map one-to-one onto a field. */
  fromRow?: (row: T) => FormValues;
  ownerId: string;
  addLabel?: string;
  intro?: ReactNode;
  orderBy?: { column: string; ascending?: boolean };
  /** Extra actions on a row (confirm, share ...). */
  extra?: (row: T) => ReactNode;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const spec = useSpecForm(fields, defaults);
  const [editing, setEditing] = useState<T | 'new' | null>(null);
  const [inlineTab, setInlineTab] = useState<'details' | 'approval' | 'history' | 'extra'>('details');
  const [saving, setSaving] = useState(false);
  const rows = useRows<T>(view, { filters, orderBy: orderBy ?? { column: 'created_at', ascending: false } });
  const mayAdd = can(profile, screenKey, 'create');
  const mayEdit = can(profile, screenKey, 'update');

  function open(row: T | 'new') {
    spec.load(row === 'new' ? null : (row as unknown as Record<string, unknown>), row !== 'new' && fromRow ? fromRow(row) : undefined);
    setInlineTab('details');
    setEditing(row);
  }

  async function save() {
    setSaving(true);
    try {
      const values = await spec.values(validate, ownerId);
      if (!values) return;
      const rows = expandLines(fields, values);
      if (editing === 'new') { for (const row of rows) await createRow(view, { ...parent, ...row }); }
      else if (editing) await saveRow(view, editing.id, rows[0]!);
      await Promise.all([view, ...invalidate].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      toast.success(editing === 'new' ? `${title}: added.` : `${title}: saved.`);
      setEditing(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="child-records">
      <div className="section-heading">
        <h3>{title} <span className="muted">({rows.data?.length ?? 0})</span></h3>
        {mayAdd && !editing && (
          <button onClick={() => open('new')} type="button"><Plus size={15} /> {addLabel ?? 'Add'}</button>
        )}
      </div>
      {intro && !editing && <p className="muted child-records-intro">{intro}</p>}

      {editing && (
        <div className="inline-form">
          {editing !== 'new' && <RecordTabs onChange={(next) => setInlineTab(next as typeof inlineTab)} tab={inlineTab} />}
          {editing !== 'new' && inlineTab === 'approval' && table && <ApprovalPanel record={{ table, id: editing.id, invalidate: [view] }} />}
          {editing !== 'new' && inlineTab === 'history' && <RecordHistory commentOn={table ? { table, id: editing.id } : undefined} entityIds={[editing.id]} />}
          {(editing === 'new' || inlineTab === 'details') && spec.render()}
          <div className="inline-form-actions" hidden={editing !== 'new' && inlineTab !== 'details'}>
            <button className="cancel" onClick={() => { setEditing(null); spec.clear(); }} type="button">Cancel</button>
            <button className="primary" disabled={saving} onClick={() => void save()} type="button">
              {saving ? 'Saving...' : editing === 'new' ? 'Save' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      <QueryState empty={emptyText} query={rows}>
        <ul className="child-record-list">
          {(rows.data ?? []).map((row) => {
            const line = summary(row);
            return (
              <li key={row.id}>
                <span className="child-record-text">
                  <strong>{line.title} {line.badge}</strong>
                  {line.detail && <small className="muted">{line.detail}</small>}
                </span>
                <span className="child-record-actions">
                  {extra?.(row)}
                  {mayEdit && !editing && (
                    <button aria-label="Edit" className="icon-only" onClick={() => open(row)} title="Edit" type="button"><Pencil size={15} /></button>
                  )}
                  <DeleteRecordButton
                    deleteFn={(reason) => saveRow(view, row.id, { is_deleted: true, delete_reason: reason })}
                    entity="feature" id={row.id} invalidate={[view, ...invalidate]} label={title.toLowerCase()} onDeleted={() => undefined} screenKey={screenKey} />
                </span>
              </li>
            );
          })}
        </ul>
      </QueryState>
    </section>
  );
}

/**
 * Add or edit one register record in a drawer (accidents, complaints, consultations, audits,
 * trades, monitoring): the same form engine as the child tables, with the record lock bar and a
 * delete that keeps the row in the audit history.
 */
export function SpecRecordDrawer<T extends { id: string }>({
  view, table, fields, defaults, editing, onClose, title, subtitle, screenKey, validate, ownerId, parent, children, deleteLabel, toolbar, extraTab, moreTabs,
}: {
  extraTab?: { label: string; content: ReactNode };
  /** Further tabs after it (a harvest's Photos, Labour, Trucks and Restocking). */
  moreTabs?: Array<{ label: string; content: ReactNode }>;
  view: string;
  /** The underlying table, for the lock bar: 'ops.accidents'. */
  table: string;
  fields: FieldSpec[];
  defaults?: FormValues;
  editing: T | null;
  onClose: () => void;
  title: string;
  subtitle?: string;
  screenKey: string;
  validate?: (form: FormValues) => Record<string, string | undefined>;
  ownerId?: string;
  parent?: Record<string, string>;
  /** Shown under the form when editing: child tables of the record. */
  children?: ReactNode;
  deleteLabel: string;
  /** Above the form: e.g. "Prefill from records", with access to the form values. */
  toolbar?: (form: FormValues, setForm: (update: (now: FormValues) => FormValues) => void) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const spec = useSpecForm(fields, defaults);
  const mayWrite = can(profile, screenKey, editing ? 'update' : 'create');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  // A saved record opens to be READ, with Edit to change it (owner, 2026-09-27: "on click on
  // datagrid ... shall show detailed view. with an option to edit"). A new one opens as the form.
  const [editMode, setEditMode] = useState(!editing);
  if (!loaded) {
    spec.load(editing as unknown as Record<string, unknown> | null);
    setLoaded(true);
  }
  function cancelEdit() {
    if (!editing) { onClose(); return; }
    spec.load(editing as unknown as Record<string, unknown>);   // what was typed is dropped
    setEditMode(false);
  }

  async function save() {
    setSaving(true);
    try {
      const values = await spec.values(validate, ownerId ?? editing?.id);
      if (!values) return;
      const rows = expandLines(fields, values);
      if (editing) await saveRow(view, editing.id, rows[0]!);
      else {
        // One row per line. A failure part way says how many went in, so nothing is typed twice.
        let done = 0;
        try {
          for (const row of rows) { await createRow(view, { ...(parent ?? {}), ...row }); done += 1; }
        } catch (error) {
          if (done > 0) await queryClient.invalidateQueries({ queryKey: [view] });
          throw new Error(`${done > 0 ? `Lines 1-${done} were saved; line ${done + 1} was not: ` : ''}${error instanceof Error ? error.message : 'it could not be saved.'}`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: [view] });
      toast.success(editing ? 'Saved.' : rows.length > 1 ? `${rows.length} records added.` : 'Added.');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <RecordDrawer
      record={editing ? { table, id: editing.id, invalidate: [view] } : null}
      footer={
        <>
          {editing && (
            <DeleteRecordButton deleteFn={(reason) => saveRow(view, editing.id, { is_deleted: true, delete_reason: reason })}
                                entity="feature" id={editing.id} invalidate={[view]} label={deleteLabel} onDeleted={onClose} screenKey={screenKey} />
          )}
          {editMode ? (
            <>
              <button className="cancel" onClick={cancelEdit} type="button">Cancel</button>
              {mayWrite && (
                <button className="primary" disabled={saving} onClick={() => void save()} type="button">
                  {saving ? 'Saving...' : editing ? 'Save changes' : 'Save'}
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={onClose} type="button">Close</button>
              {mayWrite && (
                <button className="primary" onClick={() => setEditMode(true)} type="button"><Pencil size={14} /> Edit</button>
              )}
            </>
          )}
        </>
      }
      onClose={onClose}
      open
      subtitle={mayWrite || !editing ? subtitle : 'Read only: your role can view this record but not change it.'}
      extraTab={extraTab}
      moreTabs={moreTabs}
      sections={spec.drawerSections()}
      title={title}
      wide
    >
      {editMode && mayWrite && toolbar?.(spec.form, spec.setForm)}
      {editMode
        ? <fieldset className="spec-fieldset" disabled={!mayWrite}>{spec.render(true, true)}</fieldset>
        : spec.render(true, true, true)}
      {editing && children}
    </RecordDrawer>
  );
}
